import { useState, useEffect, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { QrCode, Loader2, CheckCircle2, RefreshCw, Power, ShieldOff, Smartphone, AlertTriangle, WifiOff, Settings2, ServerCrash } from "lucide-react";

const STATUS_META = {
  init: { label: "Inizializzazione", cls: "border-slate-400/40 bg-slate-400/10 text-slate-600" },
  connecting: { label: "Connessione…", cls: "border-amber-500/40 bg-amber-500/10 text-amber-600" },
  qr_pending: { label: "In attesa di scansione", cls: "border-sky-500/40 bg-sky-500/10 text-sky-600" },
  connected: { label: "Connesso", cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-600" },
  disconnected: { label: "Disconnesso", cls: "border-rose-500/40 bg-rose-500/10 text-rose-600" },
  revoked: { label: "Revocato", cls: "border-rose-500/40 bg-rose-500/10 text-rose-600" },
};

const GW_META = {
  not_configured: { title: "QR Gateway non configurato", icon: Settings2, cls: "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400" },
  disabled: { title: "Provider QR disabilitato", icon: Settings2, cls: "border-slate-400/40 bg-slate-400/10 text-slate-600" },
  offline: { title: "QR Gateway offline", icon: WifiOff, cls: "border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-400" },
  circuit_open: { title: "QR Gateway temporaneamente non disponibile", icon: ServerCrash, cls: "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400" },
};

export default function QrGatewayConnect() {
  const [channelId, setChannelId] = useState("qr-primary");
  const [session, setSession] = useState(null);
  const [starting, setStarting] = useState(false);
  const [polling, setPolling] = useState(false);
  const [gw, setGw] = useState(null); // stato "soft" del gateway
  const status = session?.status || "init";
  const meta = STATUS_META[status] || STATUS_META.init;
  const gatewayReady = gw?.state === "online";
  const gwBlocked = gw && gw.state !== "online";

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/communications/qr-gateway/status");
      setGw(data.data);
      return data.data;
    } catch (e) {
      setGw({ state: "offline", message: "Impossibile determinare lo stato del QR Gateway." });
      return null;
    }
  }, []);

  const fetchSession = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/communications/qr-gateway/session?channel_id=${encodeURIComponent(channelId)}`);
      setSession(data.data);
      return data.data;
    } catch (e) {
      return null;
    }
  }, [channelId]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);
  useEffect(() => { if (gatewayReady) fetchSession(); }, [gatewayReady, fetchSession]);

  const refreshAll = useCallback(async () => {
    const s = await fetchStatus();
    if (s?.state === "online") fetchSession();
  }, [fetchStatus, fetchSession]);

  // Polling live finché non connesso / revocato / disconnesso
  useEffect(() => {
    if (!polling) return;
    const stop = ["connected", "revoked"].includes(status);
    if (stop) {
      setPolling(false);
      if (status === "connected") toast.success("Sessione WhatsApp connessa!");
      return;
    }
    const t = setInterval(fetchSession, 2500);
    return () => clearInterval(t);
  }, [polling, status, fetchSession]);

  const startSession = async () => {
    if (!gatewayReady) { toast.error("QR Gateway non disponibile in questo ambiente."); return; }
    setStarting(true);
    try {
      await api.post("/admin/communications/qr-gateway/connect", { channel_id: channelId, label: "QR primario" });
      toast.success("Sessione avviata — genero il QR…");
      setPolling(true);
      setTimeout(fetchSession, 1500);
    } catch (e) { toast.error(apiError(e)); }
    finally { setStarting(false); }
  };

  const doAction = async (path, msg) => {
    try {
      await api.post(`/admin/communications/qr-gateway/${path}?channel_id=${encodeURIComponent(channelId)}`);
      toast.success(msg);
      setPolling(false);
      fetchSession();
    } catch (e) { toast.error(apiError(e)); }
  };

  const qrUrl = status === "qr_pending" ? session?.qr?.dataUrl : null;
  const connected = status === "connected";

  return (
    <div data-testid="qr-gateway-page">
      <PageHeader
        title="QR Gateway WhatsApp"
        subtitle="Collega un numero WhatsApp scansionando il QR con il telefono (provider self-hosted)"
        icon={QrCode}
        actions={
          <Badge variant="outline" className={meta.cls} data-testid="qr-status-badge">{meta.label}</Badge>
        }
      />

      {gwBlocked && (() => {
        const gm = GW_META[gw.state] || GW_META.offline;
        const GIcon = gm.icon || AlertTriangle;
        return (
          <div className={`mb-6 flex items-start gap-3 rounded-xl border p-4 ${gm.cls}`} data-testid="qr-gateway-banner">
            <GIcon className="mt-0.5 h-5 w-5 shrink-0" />
            <div className="space-y-1">
              <p className="text-sm font-semibold">{gm.title}</p>
              <p className="text-sm opacity-90">{gw.message}</p>
              <p className="text-xs opacity-80">
                Gli altri provider (es. <b>Meta Cloud API</b>) restano disponibili. Configura un gateway esterno per usare il provider QR in produzione — vedi <code>qr-gateway/DEPLOY.md</code>.
              </p>
            </div>
          </div>
        );
      })()}

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2" data-testid="qr-display-card">
          <CardHeader><CardTitle className="text-base">Scansiona per collegare</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-col items-center gap-5 py-4">
              <div className="flex h-[320px] w-[320px] items-center justify-center rounded-2xl border-2 border-dashed border-border bg-muted/30 p-4">
                {connected ? (
                  <div className="flex flex-col items-center gap-3 text-center" data-testid="qr-connected">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/10">
                      <CheckCircle2 className="h-9 w-9 text-emerald-500" />
                    </div>
                    <p className="font-medium text-foreground">WhatsApp collegato</p>
                    {session?.phoneNumber && (
                      <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                        <Smartphone className="h-4 w-4" /> {session.phoneNumber}
                      </p>
                    )}
                  </div>
                ) : qrUrl ? (
                  <img src={qrUrl} alt="QR WhatsApp" className="h-full w-full rounded-lg" data-testid="qr-image" />
                ) : polling ? (
                  <div className="flex flex-col items-center gap-3 text-center text-muted-foreground">
                    <Loader2 className="h-8 w-8 animate-spin" />
                    <p className="text-sm">Generazione QR in corso…</p>
                  </div>
                ) : gwBlocked ? (
                  <div className="flex flex-col items-center gap-3 text-center text-muted-foreground" data-testid="qr-blocked-placeholder">
                    <WifiOff className="h-10 w-10" />
                    <p className="text-sm px-4">{(GW_META[gw.state] || GW_META.offline).title}</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3 text-center text-muted-foreground">
                    <QrCode className="h-10 w-10" />
                    <p className="text-sm">Premi "Genera QR" per iniziare</p>
                  </div>
                )}
              </div>

              {!connected && (
                <p className="max-w-sm text-center text-xs text-muted-foreground">
                  Apri WhatsApp sul telefono → <b>Impostazioni</b> → <b>Dispositivi collegati</b> → <b>Collega un dispositivo</b> e inquadra il QR. Il codice si rigenera automaticamente ogni ~20 secondi.
                </p>
              )}

              <div className="flex flex-wrap justify-center gap-2">
                {!connected && (
                  <Button onClick={startSession} disabled={starting || !gatewayReady} className="rounded-lg" data-testid="qr-generate-btn">
                    {starting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <QrCode className="mr-2 h-4 w-4" />}
                    {status === "qr_pending" || polling ? "Rigenera QR" : "Genera QR"}
                  </Button>
                )}
                <Button variant="outline" onClick={refreshAll} className="rounded-lg" data-testid="qr-refresh-btn">
                  <RefreshCw className="mr-2 h-4 w-4" /> Aggiorna stato
                </Button>
                {connected && (
                  <Button variant="outline" onClick={() => doAction("disconnect", "Sessione disconnessa")} className="rounded-lg" data-testid="qr-disconnect-btn">
                    <Power className="mr-2 h-4 w-4" /> Disconnetti
                  </Button>
                )}
                {(connected || status === "qr_pending") && (
                  <Button variant="ghost" onClick={() => doAction("revoke", "Sessione revocata")} className="rounded-lg text-rose-600 hover:text-rose-700" data-testid="qr-revoke-btn">
                    <ShieldOff className="mr-2 h-4 w-4" /> Revoca
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card data-testid="qr-session-info">
            <CardHeader><CardTitle className="text-base">Dettagli sessione</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>ID canale</Label>
                <Input value={channelId} onChange={(e) => setChannelId(e.target.value)} disabled={polling || connected} data-testid="qr-channel-input" />
                <p className="text-xs text-muted-foreground">Identificatore della sessione (session_id = tenant:canale).</p>
              </div>
              <div className="rounded-lg bg-muted/40 p-3 text-sm">
                <div className="flex items-center justify-between py-1"><span className="text-muted-foreground">Stato</span><span className="font-medium">{meta.label}</span></div>
                <div className="flex items-center justify-between py-1"><span className="text-muted-foreground">Numero</span><span className="font-medium">{session?.phoneNumber || "—"}</span></div>
                {session?.lastError?.code && (
                  <div className="flex items-center justify-between py-1"><span className="text-muted-foreground">Ultimo errore</span><span className="font-medium text-rose-600">{session.lastError.code}</span></div>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Il browser non comunica mai direttamente con il Gateway: tutto passa dal Core (FastAPI) via chiamate firmate HMAC.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
